import { z } from 'zod'
import { errorParentIsRemoved, Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import type { Coassign_Assign_Insert_Input, Coassign_Assign_Set_Input } from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { formatISO } from 'date-fns'
import { pick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'
import { isExist } from '@/lib/relation-graph'

export const assignSchema = z.object({
  id: z.string().max(21),
  project_id: z.string().max(21).refine(isExist('project'), errorParentIsRemoved),
  user_id: z.string().max(21).refine(isExist('user'), errorParentIsRemoved),
  job_title_id: z.string().max(21).refine(isExist('job_title'), errorParentIsRemoved),
  comment: z.string().max(5000).nullable().default(null),
  visible: z.boolean(),
  icon: z.string().max(20).nullable().default(null)
})

export type Assign = z.infer<typeof assignSchema>

export class AssignMutator extends Mutator<typeof assignSchema> {
  static readonly stageableClassName = 'AssignMutator'
  schema = assignSchema
  table = 'assign' as const

  static async create(id: string, project_id: string): Promise<Reactive<AssignMutator>> {
    const self = this.createInstance()
    // 現在 visible は使っておらず、true 固定とする
    self.context = { id, project_id, visible: true }
    return self
  }

  static async load(id: string): Promise<Reactive<AssignMutator>> {
    const self = this.createInstance()
    // FIXME: need use watch query
    const base = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirstOrThrow()
    self.base = { ...base, ...{ visible: Boolean(base.visible) } }
    // 現在 visible は使っておらず、true 固定とする
    self.context = { id, project_id: base.project_id, visible: true }
    return self
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const sqliteParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [
        key,
        typeof value === 'boolean' ? Number(value) : value
      ])
    )

    const queries = []
    // ユーザが変更される場合は、work_on_project を削除する
    if (this.base && params.user_id && params.user_id !== this.base.user_id) {
      queries.push(DataBase.deleteFrom('work_on_project').where('assign_id', '=', id).compile())
    }
    queries.push(DataBase.updateTable(this.table).set(sqliteParams).where('id', '=', id).compile())
    return queries
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const tenant_id = useUserStore().userInfo!.tenantId
    const params = { ...(await this.parsed)!, ...{ tenant_id } }
    const sqliteParams = { ...params, ...{ visible: Number(params.visible) } }
    return [DataBase.insertInto(this.table).values(sqliteParams).compile()]
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    const id = this.context.id!
    const workOnProjectDelete = DataBase.deleteFrom('work_on_project')
      .where('assign_id', '=', id)
      .compile()
    const assignPlanDelete = DataBase.deleteFrom('assign_plan')
      .where('assign_id', '=', id)
      .compile()
    return [
      workOnProjectDelete,
      assignPlanDelete,
      DataBase.deleteFrom(this.table).where('id', '=', id).compile()
    ]
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const params = (await this.parsed)!
    const variables: Coassign_Assign_Insert_Input = { ...params, ...{ commit_id } }
    return [{ mutation: insertAssignMutationDocument, variables: { input: variables } }]
  }

  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const variables = {
      commit_id: commitId,
      deleted_at: formatISO(new Date())
    }
    return [
      {
        mutation: updateDeletedAtWorkOnProjectFromAssignMutationDocument,
        variables: { assignId: id, input: variables }
      },
      { mutation: updateAssignPlanMutationDocument, variables: { assignId: id, input: variables } },
      { mutation: updateAssignMutationDocument, variables: { id: id, input: variables } }
    ]
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const variables: Coassign_Assign_Set_Input = { ...params, ...{ commit_id } }

    const queries = []
    // ユーザが変更される場合は、work_on_project を削除する
    if (this.base && params.user_id && params.user_id !== this.base.user_id) {
      queries.push({
        mutation: updateDeletedAtWorkOnProjectFromAssignMutationDocument,
        variables: {
          assignId: id,
          input: {
            commit_id: commit_id,
            deleted_at: formatISO(new Date())
          }
        }
      })
    }
    queries.push({
      mutation: updateAssignMutationDocument,
      variables: { id: id, input: variables }
    })
    return queries
  }
}

const insertAssignMutationDocument = graphql(/* GraphQL */ `
  mutation insertAssign($input: coassign_assign_insert_input!) {
    insert_coassign_assign_one(object: $input) {
      created_at
    }
  }
`)

const updateAssignMutationDocument = graphql(/* GraphQL */ `
  mutation updateAssign($id: String!, $input: coassign_assign_set_input!) {
    update_coassign_assign_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)

const updateAssignPlanMutationDocument = graphql(/* GraphQL */ `
  mutation updateAssignPlanByAssignId($assignId: String!, $input: coassign_assign_plan_set_input!) {
    update_coassign_assign_plan(
      where: { assign_id: { _eq: $assignId }, deleted_at: { _is_null: true } }
      _set: $input
    ) {
      returning {
        updated_at
      }
    }
  }
`)

const updateDeletedAtWorkOnProjectFromAssignMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtWorkOnProjectFromAssignMutationDocument(
    $assignId: String!
    $input: coassign_work_on_project_set_input!
  ) {
    update_coassign_work_on_project(
      where: { assign_id: { _eq: $assignId }, deleted_at: { _is_null: true } }
      _set: $input
    ) {
      affected_rows
    }
  }
`)
